import { Card, Badge } from '@atoms/index';
import { useRestaurantFinanceSummary, useRestaurantLedger } from '@/hooks/useFinance';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@/utils/cn';

function parseMoneyString(value: string | undefined): number {
  const parsed = Number(value ?? '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTxType(type: string) {
  if (type === 'PAYOUT') return 'Satış';
  if (type === 'COMMISSION') return 'Komisyon';
  if (type === 'DELIVERY_FEE') return 'Kurye Ücreti';
  if (type === 'CASH_COLLECTION_DEBT') return 'Nakit Tahsilat';
  return type;
}

export default function FinanceTab() {
  const summary = useRestaurantFinanceSummary();
  const ledger = useRestaurantLedger();

  const pendingPayout = parseMoneyString(summary.data?.pendingPayoutAmount);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-500 mb-1">Bekleyen Ödeme</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(pendingPayout)}</div>
          <div className="text-xs text-gray-500 mt-1">Platformdan tahsil edilecek net tutar.</div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Finans Hareketleri</h3>
            <p className="text-sm text-gray-600">Ledger kayıtları (restorana göre filtrelenmiş)</p>
          </div>
          {ledger.isLoading && <Badge variant="info">Yükleniyor</Badge>}
          {ledger.error && <Badge variant="warning">Bağlantı Hatası</Badge>}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left font-semibold px-6 py-3">Tarih</th>
                <th className="text-left font-semibold px-6 py-3">Order Ref</th>
                <th className="text-left font-semibold px-6 py-3">Tip</th>
                <th className="text-right font-semibold px-6 py-3">Tutar</th>
                <th className="text-right font-semibold px-6 py-3">Bakiye</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!ledger.isLoading && !ledger.error && (ledger.data?.transactions?.length ?? 0) === 0 && (
                <tr>
                  <td className="px-6 py-10 text-center text-gray-500" colSpan={5}>
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}

              {(ledger.data?.transactions ?? []).map((tx) => {
                const amount = parseMoneyString(tx.amount);
                const isCommission = tx.type === 'COMMISSION';

                return (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-gray-700 whitespace-nowrap">
                      {new Date(tx.createdAt).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-6 py-3 text-gray-700">
                      {tx.orderId ? tx.orderId.slice(0, 8) : '—'}
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={isCommission ? 'warning' : 'success'}>
                        {formatTxType(tx.type)}
                      </Badge>
                    </td>
                    <td className={cn('px-6 py-3 text-right font-semibold', isCommission ? 'text-danger' : 'text-success')}>
                      {formatCurrency(isCommission ? -Math.abs(amount) : Math.abs(amount))}
                    </td>
                    <td className="px-6 py-3 text-right text-gray-500">—</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

